<template>
  <div class='p-3 vstack gap-3 bg-light'>
    <template v-if='resultOrchestrators.length > 0'>
      <div v-if="votingRoundReport.status == 'finished' && resultOrchestrators.length != 1" class='p-3 bg-white rounded shadow-sm'>
        <h3>{{ $t("backend.e2e.voting_round_reports.show.overall_turnout") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <TurnoutTable
          :turnout='votingRoundReport.turnout' />
      </div>

      <div v-if="contestsToMap" class='p-3 bg-white rounded shadow-sm'>
        <h3>{{ $t("backend.e2e.voting_round_reports.show.write_in_mapping") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <p>{{ $t("backend.e2e.voting_round_reports.show.write_in_mapping_description") }}</p>
        <button v-if="contestsToMap > 1" class="btn btn-secondary" @click="skipMapping">
          {{ $t("backend.e2e.voting_round_reports.show.mapping_btn") }}
        </button>
      </div>

      <ResultOrchestrator
        v-for="resultOrchestrator in resultOrchestrators"
        :key="`result_orchestrator_${resultOrchestrator.id}`"
        :result-orchestrator="resultOrchestrator"
        :trustees="trustees"
        :voting-round-report="votingRoundReport"
        :contest="contestFor(resultOrchestrator.contestReference)"
        :threshold="orchestrator.threshold"
      />
    </template>
    <div v-else class="p-3 bg-white rounded shadow-sm">
      <template v-if='votingRoundReport.status == "starting"'>
        <h3>{{ $t("backend.e2e.voting_round_reports.show.ceremony_starting") }}</h3>
        <div class='accent-line mt-2 mb-4'></div>
      </template>
      <div class='text-center'>
        <AVSpinner size='md' style='width: 3rem; height: 3rem' color='dark' />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import type { ConferenceContest, ConferenceVotingRoundReport, ResultOrchestrator as IResultOrchestrator } from '@/types';
import { CeremonyChannelSubscription } from '@/channels/CeremonyChannel';
import ResultOrchestrator from '@/components/backend/ResultOrchestrator.vue';
import TurnoutTable from '@/components/backend/live/TurnoutTable.vue';
import apiClient from '@/entrypoints/frontend/apiClient';

export default defineComponent({
  name: 'ResultCeremony',
  components: { TurnoutTable, ResultOrchestrator },
  props: {
    initData: Object,
    orchestratorId: {
      type: Number,
      required: true,
    },
    reportId: {
      type: Number,
      required: true,
    },
    refreshOnFinish: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      orchestrator: {} as IResultOrchestrator,
      resultOrchestrators: [],
      contests: [],
      votingRoundReport: {} as ConferenceVotingRoundReport,
      trustees: [],
    };
  },
  computed: {
    contestsToMap() {
      return this.resultOrchestrators.filter((ro: IResultOrchestrator) => ro.status === "mapping").length;
    },
  },
  methods: {
    handleReceived(data) {
      switch (data.type) {
        case 'full_update':
          this.contests = data.state.contests
          this.orchestrator = data.state.orchestrator;
          this.votingRoundReport = data.state.votingRoundReports.find((report) => report.id === this.reportId) ;
          this.resultOrchestrators = this.votingRoundReport.resultOrchestrators;
          this.trustees = data.state.trustees;
          break;
        default:
          this.subscription.fetchStatus('status');
      }
    },
    contestFor(contestReference: string) {
      return this.contests.find((c: ConferenceContest) => c.reference == contestReference)
    },
    skipMapping() {
      const postRequests = this.resultOrchestrators.map((ro: IResultOrchestrator) => {
        if (ro.status === "mapping") {
          const url = `${window.location.href.split('/voting_rounds/')[0]}/e2e/result_orchestrators/${ro.id}/create_mapping`;
          const filteredMapping: Record<string, Record<string, string>> = {};
          Object.entries(ro.writeIns).forEach(([reference]) => filteredMapping[reference] = {});
          return apiClient.post(url, { write_in_mapping: filteredMapping });
        }
      });
      Promise.all(postRequests).catch(error => console.error(error));
    },
  },
  watch: {
    votingRoundReport(newVal, oldVal) {
      if (this.refreshOnFinish && newVal.status == "finished" && oldVal.status == "aggregating") {
        setTimeout(() => window.location.reload(), 500);
      }
    }
  },
  mounted() {
    if(this.initData) {
      this.orchestrator = this.initData.orchestrator;
      this.votingRoundReport = Array.isArray(this.initData.votingRoundReports) ? this.initData.votingRoundReports.find((report) => report.id === this.reportId) : this.initData.votingRoundReports
      this.resultOrchestrators = this.votingRoundReport.resultOrchestrators;
      this.trustees = this.initData.trustees;
      this.contests = this.initData.contests
    }

    this.subscription = new CeremonyChannelSubscription(this.orchestratorId, {
      received: this.handleReceived,
    }, this.votingRoundReport.id);
  },
  unmounted(){
    this.subscription?.unsubscribe();
  }
});
</script>
